import type { User } from 'oidc-client-ts';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router';
import { config } from './config';
import { main } from './configuration';
import { handleLoginRedirect } from './configuration/login/redirect';
import { store } from './configuration/setup/store';
import { InvalidLogin } from './features/app/InvalidLogin';
import { DEFAULT_LOCALE } from './configuration/lang/lang';
import ErrorFallback from './components/ErrorFallback';
import { router } from './routes/Router';
import { IntlProvider } from 'react-intl';
import { useDisplayMessages, useLocale } from './configuration/lang/langSlice.ts';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';

const App = () => {
    const userLocale = useLocale();
    const displayMessages = useDisplayMessages();

    if (!(displayMessages && userLocale)) {
        return null;
    }

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ErrorBoundary fallback={<ErrorFallback />}>
                <RouterProvider router={router} />
            </ErrorBoundary>
        </IntlProvider>
    );
};

const renderApplication = ({ user }: { user: User | null; logout: () => void }) => {
    // biome-ignore lint/style/noNonNullAssertion: root is definitely there
    const container = document.getElementById('root')!;
    const root = createRoot(container);

    let showApp = true;
    try {
        showApp = user?.profile.sub.startsWith('azure-my-rio-de:') ?? false;
    } catch (e) {
        console.error(e);
    }

    // Note that we need to use the base "Router" with a "hash" history
    // because the "HashRouter" doesn't allow handing in a history
    // from the outside. So this is effectively a "HashRouter" despite
    // that not being obvious here
    if (showApp) {
        root.render(
            <Provider store={store}>
                <App />
            </Provider>
        );
    } else {
        root.render(<InvalidLogin idToken={user?.id_token} />);
    }
};

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;
const isProdPreview = import.meta.env.VITE_PRODUCTION_PREVIEW;

if ((isDev && config.enableMockServer) || isProdPreview) {
    import('../mocks/msw/browser').then(({ startWorker }) => startWorker()).then(() => main(renderApplication));
}

if (window.location.href.startsWith(config.login.redirectUri as string)) {
    handleLoginRedirect();
} else if (isDev && !config.enableMockServer) {
    void main(renderApplication);
} else if (isProd && !isProdPreview) {
    void main(renderApplication);
}
